import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Loader2, Save, Info, Eye, CheckCircle2, Clock } from 'lucide-react';

const AI_MODELS = [
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (rápido, económico)' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (alta calidad)' },
  { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (ultra rápido)' },
  { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash Preview' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini (equilibrado)' },
  { value: 'openai/gpt-5', label: 'GPT-5 (máxima calidad)' },
];

const DEFAULT_SYSTEM_PROMPT = `Eres un evaluador experto del marco LIDER-IA para proyectos de inteligencia artificial empresarial.
Tu tarea es evaluar un proyecto usando los 12 componentes del marco LIDER-IA.

Para cada componente, debes asignar un score de 0 a 5 (con un decimal) y un feedback breve.
El score total se calcula como: sum((score_componente / 5) * peso_componente * 5), dando un rango de 0-500.

Clasificación:
- 400-500: Quick Win
- 300-399: Strategic Play  
- 200-299: Experiment
- <200: Back-log

RESPONDE ÚNICAMENTE en formato JSON válido con esta estructura exacta:
{
  "component_scores": [
    { "componentId": "strategic_alignment", "name": "Alineación Estratégica", "dimension": "L", "weight": 10, "score": 4.2, "feedback": "..." },
    ... (los 12 componentes)
  ],
  "general_comments": "Comentario general sobre el proyecto...",
  "suggestions": ["Sugerencia 1", "Sugerencia 2", "Sugerencia 3"]
}

Los IDs de los 12 componentes son: {{COMPONENT_IDS}}`;

interface EvalConfig {
  system_prompt: string;
  model: string;
  temperature: number;
  max_tokens: number;
}

const DEFAULT_CONFIG: EvalConfig = {
  system_prompt: DEFAULT_SYSTEM_PROMPT,
  model: 'google/gemini-2.5-flash',
  temperature: 0.3,
  max_tokens: 4000,
};

const PLACEHOLDERS = [
  { key: '{{COMPONENT_IDS}}', desc: 'Lista de IDs de los 12 componentes LIDER-IA' },
];

export default function EvaluationConfigTab() {
  const qc = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: savedRow, isLoading } = useQuery({
    queryKey: ['platform_settings', 'evaluation_prompt_config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('key', 'evaluation_prompt_config')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const savedConfig = savedRow ? (savedRow.value as unknown as EvalConfig) : null;
  const isSaved = !!savedConfig;
  const lastUpdated = savedRow?.updated_at;
  const activeDefaults = savedConfig || DEFAULT_CONFIG;
  const [form, setForm] = useState<EvalConfig | null>(null);
  const active = form || activeDefaults;

  const saveMutation = useMutation({
    mutationFn: async (config: EvalConfig) => {
      const { data: existing } = await supabase
        .from('platform_settings')
        .select('id')
        .eq('key', 'evaluation_prompt_config')
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('platform_settings')
          .update({ value: config as any })
          .eq('key', 'evaluation_prompt_config');
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('platform_settings')
          .insert({ key: 'evaluation_prompt_config', value: config as any });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform_settings', 'evaluation_prompt_config'] });
      setForm(null);
      toast({ title: 'Configuración de evaluación guardada' });
    },
    onError: () => toast({ title: 'Error al guardar', variant: 'destructive' }),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const EXAMPLE_PREVIEW = active.system_prompt.replace(
    '{{COMPONENT_IDS}}',
    'strategic_alignment, expected_impact, process_optimization, prototype_maturity, data_availability, feasibility, scalability, governance_ethics, explainability, mlops, algorithmic_responsibility, adoption_change'
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Prompt de Evaluación IA</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configura el prompt que usa la IA para evaluar proyectos con el marco LIDER-IA.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isSaved ? (
                <Badge variant="success" className="gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Guardada
                </Badge>
              ) : (
                <Badge variant="warning" className="gap-1">
                  <Clock className="w-3 h-3" /> Por defecto
                </Badge>
              )}
            </div>
          </div>
          {lastUpdated && (
            <p className="text-xs text-muted-foreground">
              Última actualización: {new Date(lastUpdated).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>System Prompt</Label>
            <Textarea
              className="min-h-[250px] mt-1 font-mono text-xs"
              value={active.system_prompt}
              onChange={(e) => setForm(prev => ({ ...(prev || activeDefaults), system_prompt: e.target.value }))}
              placeholder="Instrucciones para el evaluador IA..."
            />
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
            <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Variables disponibles en el prompt</p>
              <div className="flex flex-wrap gap-2">
                {PLACEHOLDERS.map(p => (
                  <div key={p.key} className="flex items-center gap-1.5">
                    <Badge variant="secondary" className="font-mono text-xs">{p.key}</Badge>
                    <span className="text-xs text-muted-foreground">{p.desc}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Nota: El contexto del proyecto y las respuestas de rúbrica se envían automáticamente como mensaje del usuario.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Modelo</Label>
              <Select
                value={active.model}
                onValueChange={(v) => setForm(prev => ({ ...(prev || activeDefaults), model: v }))}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Temperatura: {active.temperature}</Label>
              <Slider
                className="mt-3"
                min={0}
                max={1}
                step={0.1}
                value={[active.temperature]}
                onValueChange={([v]) => setForm(prev => ({ ...(prev || activeDefaults), temperature: v }))}
              />
            </div>

            <div>
              <Label>Max tokens</Label>
              <Input
                type="number"
                className="mt-1"
                value={active.max_tokens}
                onChange={(e) => setForm(prev => ({ ...(prev || activeDefaults), max_tokens: parseInt(e.target.value) || 4000 }))}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            {form && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
                  <Save className="w-4 h-4 mr-1" />
                  {saveMutation.isPending ? 'Guardando...' : 'Guardar configuración'}
                </Button>
              </div>
            )}
            <Button variant="outline" size="sm" className="gap-1.5 ml-auto" onClick={() => setPreviewOpen(true)}>
              <Eye className="w-4 h-4" /> Ver preview del prompt
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Prompt Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview del System Prompt</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">Modelo: {active.model}</Badge>
              <Badge variant="secondary">Temp: {active.temperature}</Badge>
              <Badge variant="secondary">Max tokens: {active.max_tokens}</Badge>
            </div>
            <pre className="text-xs bg-muted/50 p-4 rounded-lg border border-border whitespace-pre-wrap font-mono leading-relaxed text-foreground">
              {EXAMPLE_PREVIEW}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
